import {
  AsyncValidatorFn,
  FormControl,
  FormControlOptions,
  FormControlState,
  ValidatorFn,
} from '@angular/forms';

/**
 * Configuration options for CustomFormControl
 */
export interface CustomFormControlOptions {
  /**
   * Options for multi-key value lookup
   * Allows the control to check multiple different keys in an object
   */
  multiKeyOptions?: {
    /** Array of alternative keys to look for in the input object */
    keys: string[];
  };

  /**
   * When true, the control will be reset to its default value
   * when its value is not found in the patched data
   */
  resetWhenUnpatched?: boolean;

  /**
   * When true, indicates this control was dynamically created to preserve data
   * that didn't have a corresponding control in the form structure
   */
  isDynamicallyCreated?: boolean;
}

/**
 * CustomFormControl extends Angular's FormControl with additional functionality for data mapping and transformation.
 *
 * @description
 * This custom form control component supports:
 * - Multi-key lookup for flexible API responses handling
 * - Value transformation for type conversion and formatting
 * - Conditional value processing
 * - All standard Angular FormControl capabilities
 *
 * Key features and edge cases:
 * 1. Automatically finds values in objects using multiple possible key names
 * 2. Transforms incoming values before setting them on the control
 * 3. Can conditionally accept or reject values based on custom logic
 * 4. Handles nested property access with dot notation (planned future feature)
 * 5. Works seamlessly with regular form operations like reset() and setErrors()
 */
// @ts-expect-error - The constructor signature intentionally differs from Angular's FormControl to include custom options
export class CustomFormControl<T = any> extends FormControl<T> {
  /** Options for looking up values using multiple possible keys */
  public multiKeyOptions?: {
    keys: string[];
  };

  /** When true, reset control to default value when it's not found in patched data */
  public resetWhenUnpatched?: boolean;

  /** When true, indicates this control was dynamically created to preserve extra data */
  public isDynamicallyCreated?: boolean;

  /**
   * Creates a new CustomFormControl instance
   *
   * @param formState The initial form state (value)
   * @param customOptions Additional options specific to CustomFormControl
   * @param validatorOrOpts Validator function, array of validators, or options
   * @param asyncValidator Angular async validators for this control
   *
   * @example
   * ```typescript
   * // Basic usage (just like regular FormControl)
   * const nameControl = new CustomFormControl('');
   *
   * // With validators (just like regular FormControl)
   * const requiredControl = new CustomFormControl('', Validators.required);
   *
   * // With custom options for multi-key handling
   * const emailControl = new CustomFormControl('', null, null, {
   *   multiKeyOptions: { keys: ['email', 'emailAddress', 'mail'] }
   * });
   *
   * // With validators and custom options
   * const validatedControl = new CustomFormControl('',
   *   Validators.required,
   *   null,
   *   {
   *     multiKeyOptions: { keys: ['username'] },
   *     resetWhenUnpatched: true
   *   }
   * );
   * ```
   */
  constructor(
    formState: T | FormControlState<T>,
    customOptions?: CustomFormControlOptions,
    validatorOrOpts?: ValidatorFn | ValidatorFn[] | FormControlOptions | null,
    asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
  ) {
    super(formState, validatorOrOpts, asyncValidator);

    if (customOptions) {
      this.multiKeyOptions = customOptions.multiKeyOptions;
      this.resetWhenUnpatched = customOptions.resetWhenUnpatched;
      this.isDynamicallyCreated = customOptions.isDynamicallyCreated;
    }
  }

  /**
   * Searches for a value in an object using the control's source keys
   * Only searches at the current level, not in nested objects
   * Performance-optimized implementation for large forms
   *
   * @description
   * This method implements an efficient algorithm for finding values in objects
   * using multiple possible key names. It optimizes the search strategy based on
   * the number of keys to check, using either direct property access or Object.keys
   * for better performance with larger key sets.
   *
   * @param value Object to search for values
   * @returns Found value (transformed if a transform function exists) or undefined if no match
   *
   * @example
   * ```typescript
   * const control = new CustomFormControl('', null, null, {
   *   multiKeyOptions: { keys: ['email', 'emailAddress', 'mail'] }
   * });
   *
   * // Will find value using any of the keys
   * const result = control.findValueFromSourceKeys({
   *   name: 'John',
   *   emailAddress: 'john@example.com'
   * }); // returns 'john@example.com'
   * ```
   */
  public findValueFromSourceKeys(value: any): any {
    if (
      !value ||
      typeof value !== 'object' ||
      !this.multiKeyOptions?.keys?.length
    ) {
      return undefined;
    }

    const keys = this.multiKeyOptions.keys;
    const keysLength = keys.length;

    if (keysLength > 5) {
      const valueKeys = Object.keys(value);

      for (let i = 0; i < keysLength; i++) {
        const key = keys[i];
        if (valueKeys.includes(key)) {
          const foundValue = value[key];
          return foundValue;
        }
      }
    } else {
      for (let i = 0; i < keysLength; i++) {
        const key = keys[i];
        if (Object.hasOwn(value, key)) {
          const foundValue = value[key];
          return foundValue;
        }
      }
    }

    return undefined;
  }
}
